import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { ChevronDown, Check, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDismissOnOutside } from "@/hooks/use-dismiss-on-outside";
import type { DismissReason } from "@/hooks/use-dismiss-on-outside";
import { useDropdownPlacement } from "@/hooks/use-dropdown-placement";

/** Search box, the option list and the Confirm footer — what the flip is measured against. */
const PANEL_PREFERRED_HEIGHT = 400;

type CostEstimatorMultiSelectProps<T> = {
  data: T[];
  enableSelectAll?: boolean;
  searchKey?: keyof T;
  displayKey: keyof T;
  lookupKey: keyof T;
  selected: T[];
  onChange: (selected: T[]) => void;
  defaultValues?: T[];
  placeholder?: string;
  enableSearch?: boolean;
  atLeastOneRequired?: boolean;
};

export default function CostEstimatorMultiSelect<
  T extends Record<string, unknown>,
>({
  data,
  enableSelectAll = false,
  searchKey,
  displayKey,
  lookupKey,
  selected,
  onChange,
  defaultValues = [],
  placeholder = "Select...",
  enableSearch = false,
  atLeastOneRequired = false,
}: CostEstimatorMultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The option the keyboard is on. Focus stays in the search box; this is surfaced to
  // assistive tech through aria-activedescendant.
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const { placement, maxHeight } = useDropdownPlacement(triggerRef, isOpen, {
    preferredHeight: PANEL_PREFERRED_HEIGHT,
  });

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleDismiss = useCallback(
    (reason: DismissReason) => {
      close();
      // Only reclaim focus for Escape — on an outside click the user has already moved on.
      if (reason === "escape") triggerRef.current?.focus();
    },
    [close],
  );

  useDismissOnOutside(containerRef, isOpen, handleDismiss);

  // Apply default values only once on mount
  useEffect(() => {
    if (defaultValues.length > 0 && selected.length === 0) {
      onChange(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter data
  const filtered =
    enableSearch && searchKey
      ? data.filter((item) =>
          String(item[searchKey]).toLowerCase().includes(query.toLowerCase()),
        )
      : data;

  // Toggle selection (prevent unselecting last one if required)
  const toggleSelect = (item: T) => {
    const alreadySelected = selected.some(
      (s) => s[lookupKey] === item[lookupKey],
    );
    if (alreadySelected) {
      if (atLeastOneRequired && selected.length === 1) return; // don't allow clearing last
      onChange(selected.filter((s) => s[lookupKey] !== item[lookupKey]));
    } else {
      onChange([...selected, item]);
    }
  };

  // Handle Select All toggle
  const handleSelectAll = () => {
    const allSelected =
      selected.length === data.length &&
      data.every((d) => selected.some((s) => s[lookupKey] === d[lookupKey]));
    if (allSelected) {
      if (atLeastOneRequired && defaultValues.length > 0) {
        onChange(defaultValues);
      } else if (atLeastOneRequired) {
        // if required but no defaultValues, keep first item
        onChange([data[0]]);
      } else {
        onChange([]);
      }
    } else {
      onChange(data);
    }
  };

  const allSelected =
    selected.length === data.length &&
    data.every((d) => selected.some((s) => s[lookupKey] === d[lookupKey]));

  // The Select All row is rendered as an option, so it occupies row 0 and shifts every item
  // down by one. Keyboard indices count rendered rows, not raw `filtered` entries.
  const showSelectAll = filtered.length > 1 && enableSelectAll;
  const rowOffset = showSelectAll ? 1 : 0;
  const rowCount = filtered.length + rowOffset;

  const activateRow = (row: number) => {
    if (showSelectAll && row === 0) handleSelectAll();
    else toggleSelect(filtered[row - rowOffset]);
  };

  const moveActive = (step: number) => {
    if (!rowCount) return;
    const next =
      activeIndex < 0 ? (step > 0 ? 0 : rowCount - 1) : activeIndex + step;
    if (next < 0 || next >= rowCount) return;
    setActiveIndex(next);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent) => {
    if (isOpen) {
      // With no search box there is nowhere else for focus to go, so the trigger drives the
      // list too. When the search box is present it holds focus and this never fires.
      handleListKeyDown(event);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex(event.key === "ArrowDown" ? 0 : rowCount - 1);
    }
  };

  const handleListKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        moveActive(-1);
        return;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        return;
      case "End":
        event.preventDefault();
        setActiveIndex(rowCount - 1);
        return;
      case "Enter":
        event.preventDefault();
        // Toggling keeps the panel open — Confirm is what dismisses it.
        if (activeIndex >= 0) activateRow(activeIndex);
        return;
      case "Tab":
        // Let focus move on, but do not leave an orphaned panel behind.
        close();
        return;
      default:
    }
  };

  // Move focus into the panel so the arrow keys have somewhere to land.
  useEffect(() => {
    if (isOpen && enableSearch) searchRef.current?.focus();
  }, [isOpen, enableSearch]);

  // Keep the keyboard-active option in view as the arrows walk past the scroll edge.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const option = document.getElementById(`${baseId}-option-${activeIndex}`);
    // jsdom does not implement scrollIntoView, hence the optional call.
    (option as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest" });
  }, [isOpen, activeIndex, baseId]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && !enableSearch && activeIndex >= 0
            ? optionId(activeIndex)
            : undefined
        }
        className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
      >
        <div className="flex-1 truncate">
          {selected.length > 0
            ? selected.map((s) => s[displayKey]).join(", ")
            : placeholder}
        </div>
        <ChevronDown aria-hidden="true" className="size-4 opacity-50" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            // flex-col + the inline maxHeight below let the list shrink instead of the panel
            // overflowing the space the placement hook allowed it.
            "absolute z-50 flex w-full min-w-32 flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80",
            placement === "top" ? "bottom-full mb-1" : "top-full mt-1",
          )}
          style={{ maxHeight }}
        >
          {/* Search (optional) */}
          {enableSearch && (
            <div className="relative p-2">
              {/* The input drops its own ring to sit flush in this box, so the box shows focus. */}
              <div className="flex items-center rounded-md border px-2 focus-within:ring-1 focus-within:ring-ring">
                <Search aria-hidden="true" className="mr-2 size-4 opacity-50" />
                <Input
                  ref={searchRef}
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-activedescendant={
                    activeIndex >= 0 ? optionId(activeIndex) : undefined
                  }
                  aria-label="Search options"
                  placeholder="Search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(-1);
                  }}
                  onKeyDown={handleListKeyDown}
                  className="h-8 border-0 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    setActiveIndex(-1);
                    // This button unmounts once the query is empty — hand focus back to the
                    // search box rather than letting it fall to the body.
                    searchRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X aria-hidden="true" className="size-4" />
                </button>
              )}
            </div>
          )}
          {/* Options */}
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-multiselectable
            className="max-h-80 min-h-0 flex-1 overflow-y-auto"
          >
            {filtered.length > 0 ? (
              <>
                {/* Select All Option */}
                {showSelectAll ? (
                  <li
                    id={optionId(0)}
                    role="option"
                    aria-selected={allSelected}
                    onMouseEnter={() => setActiveIndex(0)}
                    onClick={handleSelectAll}
                    className={`relative flex w-full cursor-pointer select-none items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-primary-extraLight hover:text-accent-foreground ${
                      allSelected ? "bg-accent text-accent-foreground" : ""
                    } ${activeIndex === 0 ? "bg-primary-extraLight" : ""}`}
                  >
                    <span>Select All</span>
                    {allSelected && <Check className="size-4" />}
                  </li>
                ) : null}
                {filtered.map((item, idx) => {
                  const isSelected = selected.some(
                    (s) => s[lookupKey] === item[lookupKey],
                  );
                  return (
                    <li
                      key={idx}
                      id={optionId(idx + rowOffset)}
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setActiveIndex(idx + rowOffset)}
                      onClick={() => toggleSelect(item)}
                      className={`relative flex w-full cursor-pointer select-none items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-primary-extraLight hover:text-accent-foreground ${
                        allSelected ? "bg-accent text-accent-foreground" : ""
                      } ${idx + rowOffset === activeIndex ? "bg-primary-extraLight" : ""}`}
                    >
                      {/* T is Record<string, unknown>, so the indexed value is `unknown`.
                          Cast rather than String() — String(undefined) would start rendering
                          the literal "undefined" where today nothing renders. */}
                      <span>{item[displayKey] as ReactNode}</span>
                      {isSelected && <Check className="size-4" />}
                    </li>
                  );
                })}
              </>
            ) : (
              <li
                role="presentation"
                className="px-3 py-2 text-sm italic text-gray-500"
              >
                No results found
              </li>
            )}
          </ul>
          {/* Confirm button */}
          <div className="border-t p-2">
            <Button className="w-full" onClick={close}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
